import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";

const API_BASE = "https://api.liftsearch.co.za/api/v1";

const CodeBlock = ({ children }) => (
  <pre className="bg-gray-900 text-green-300 text-sm rounded-lg p-4 overflow-x-auto whitespace-pre">
    {children}
  </pre>
);

const Endpoint = ({ method, path, description, params, body, response, children }) => {
  const [open, setOpen] = useState(false);
  const methodColors = {
    GET: "bg-blue-100 text-blue-700",
    POST: "bg-green-100 text-green-700",
    DELETE: "bg-red-100 text-red-700",
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${methodColors[method] || "bg-gray-100 text-gray-700"}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-gray-800 flex-1">{path}</code>
        <span className="text-sm text-gray-500 hidden md:block">{description}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-gray-200 px-4 py-4 space-y-4 bg-gray-50">
          <p className="text-sm text-gray-700">{description}</p>

          {params && (
            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Query Parameters</h4>
              <div className="space-y-1">
                {params.map((p) => (
                  <div key={p.name} className="flex items-start gap-2 text-sm">
                    <code className="text-purple-700 font-mono text-xs bg-purple-50 px-1.5 py-0.5 rounded">{p.name}</code>
                    <span className="text-gray-500">{p.type}</span>
                    {p.required && <span className="text-red-500 text-xs">required</span>}
                    <span className="text-gray-600">— {p.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {body && (
            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Request Body</h4>
              <CodeBlock>{body}</CodeBlock>
            </div>
          )}

          {response && (
            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Response</h4>
              <CodeBlock>{response}</CodeBlock>
            </div>
          )}

          {children}
        </div>
      )}
    </div>
  );
};

const NAV_ITEMS = [
  { key: "getting-started", label: "Getting Started", icon: "🚀" },
  { key: "parcels", label: "Parcels & Pricing", icon: "📦" },
  { key: "orders", label: "Orders", icon: "📋" },
  { key: "waybills", label: "Waybills", icon: "🏷️" },
  { key: "reference", label: "Status & Errors", icon: "📖" },
  { key: "shopify", label: "Shopify Integration", icon: "🛍️" },
  { key: "woocommerce", label: "WooCommerce Integration", icon: "🛒" },
];

const GettingStartedSection = () => (
  <div className="space-y-10 text-gray-700">
    <section>
      <h2 className="text-2xl font-bold text-purple-800 mb-4">Authentication</h2>
      <p className="mb-4">
        All API requests require your business API key. Include it in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">X-API-Key</code> header on every request.
      </p>
      <CodeBlock>{`curl -H "X-API-Key: biz_your_api_key_here" \\
  ${API_BASE}/orders`}</CodeBlock>
      <p className="text-sm text-gray-500 mt-3">
        Find your API key on your <a href="/business/dashboard/settings" className="text-purple-600 underline">business dashboard settings</a> page.
        Contact us if you need your key regenerated.
      </p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-purple-800 mb-4">Base URL</h2>
      <CodeBlock>{API_BASE}</CodeBlock>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-purple-800 mb-4">Rate Limits</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>100 requests/minute</strong> — general endpoints</li>
        <li><strong>20 requests/minute</strong> — order creation</li>
      </ul>
      <p className="text-sm text-gray-500 mt-2">Exceeding limits returns <code className="bg-gray-100 px-1 rounded">429 Too Many Requests</code>.</p>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-purple-800 mb-4">Response Format</h2>
      <p className="mb-3">All responses are JSON. Successful responses wrap data in a <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">data</code> key. Errors return an <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">error</code> key.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase mb-1">Success</p>
          <CodeBlock>{`{
  "data": { ... }
}`}</CodeBlock>
        </div>
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase mb-1">Error</p>
          <CodeBlock>{`{
  "error": "Order not found"
}`}</CodeBlock>
        </div>
      </div>
    </section>
  </div>
);

const ParcelsSection = () => (
  <div className="space-y-4 text-gray-700">
    <h2 className="text-2xl font-bold text-purple-800 mb-4">Parcels & Pricing</h2>
    <p className="mb-4">Fetch available parcel types and prices before creating orders.</p>
    <Endpoint
      method="GET"
      path="/parcels"
      description="List parcel types with prices"
      params={[
        { name: "distance", type: "string", desc: '"long" or "short" (default: "long")' },
      ]}
      response={`{
  "data": [
    {
      "id": 1,
      "package_type": "Courier Bag",
      "max_width": 40,
      "max_height": 50,
      "max_weight": 5,
      "prices": [
        {
          "id": 3,
          "value": 150.0,
          "price_type": "Standard"
        }
      ]
    }
  ]
}`}
    />
  </div>
);

const OrdersSection = () => (
  <div className="space-y-4 text-gray-700">
    <h2 className="text-2xl font-bold text-purple-800 mb-4">Orders</h2>
    <div className="space-y-3">
      <Endpoint
        method="POST"
        path="/orders"
        description="Create a new order"
        body={`{
  "order": {
    "parcel_id": 1,
    "price_id": 3,
    "pickup_location": "123 Main St, Johannesburg",
    "quantity": 1,
    "collection_date": "2024-12-15",
    "departure_province": 1,
    "destination_province": 2,
    "departure_town": 5,
    "destination_town": 12,
    "drop_off_location": "45 Oak Ave, Polokwane",
    "receiver_full_name": "John Doe",
    "receiver_cellphone": "0712345678",
    "parcel_description": "Documents",
    "number_of_items": 1,
    "collection_invoice_number": "INV-001"
  }
}`}
        response={`{
  "data": {
    "reference_number": "48291037",
    "status": "created",
    "delivery_status": "pending",
    "pickup_location": "123 Main St, Johannesburg",
    "drop_off_location": "45 Oak Ave, Polokwane",
    "quantity": 1,
    "payment_url": "https://www.payfast.co.za/...",
    "receiver_full_name": "John Doe",
    "receiver_cellphone": "0712345678",
    "waybills": [
      {
        "id": 101,
        "sub_reference": "48291037-01",
        "status": "pending"
      }
    ],
    ...
  }
}`}
      >
        <div>
          <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Body Parameters</h4>
          <div className="space-y-1 text-sm">
            {[
              { name: "parcel_id", desc: "Parcel type ID (from GET /parcels)", required: true },
              { name: "price_id", desc: "Price ID (from GET /parcels)", required: true },
              { name: "pickup_location", desc: "Pickup address", required: true },
              { name: "quantity", desc: "Number of parcels", required: true },
              { name: "collection_date", desc: "Pickup date (YYYY-MM-DD)" },
              { name: "departure_province", desc: "Departure province ID" },
              { name: "destination_province", desc: "Destination province ID" },
              { name: "departure_town", desc: "Departure town ID" },
              { name: "destination_town", desc: "Destination town ID" },
              { name: "drop_off_location", desc: "Delivery address" },
              { name: "receiver_full_name", desc: "Receiver name" },
              { name: "receiver_cellphone", desc: "Receiver phone number" },
              { name: "parcel_description", desc: "Description of contents" },
              { name: "number_of_items", desc: "Items inside the parcel" },
              { name: "collection_invoice_number", desc: "Your internal reference (must be unique)" },
            ].map((p) => (
              <div key={p.name} className="flex items-start gap-2">
                <code className="text-purple-700 font-mono text-xs bg-purple-50 px-1.5 py-0.5 rounded">{p.name}</code>
                {p.required && <span className="text-red-500 text-xs">required</span>}
                <span className="text-gray-600">— {p.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </Endpoint>

      <Endpoint
        method="POST"
        path="/orders/bulk"
        description="Create multiple orders in one request"
        body={`{
  "orders": [
    {
      "parcel_id": 1,
      "price_id": 3,
      "pickup_location": "123 Main St, JHB",
      "quantity": 1,
      "receiver_full_name": "Jane Doe",
      "receiver_cellphone": "0712345678"
    },
    {
      "parcel_id": 1,
      "price_id": 3,
      "pickup_location": "123 Main St, JHB",
      "quantity": 1,
      "receiver_full_name": "Bob Smith",
      "receiver_cellphone": "0798765432"
    }
  ]
}`}
        response={`{
  "data": {
    "group_reference": "GRP-39201847",
    "payment_url": "https://www.payfast.co.za/...",
    "orders": [ ... ]
  }
}`}
      />

      <Endpoint
        method="GET"
        path="/orders"
        description="List your orders"
        params={[
          { name: "status", type: "string", desc: '"created", "paid", or "cancelled"' },
          { name: "delivery_status", type: "string", desc: '"pending", "collected", "enroute", "ready_for_collection", "delivered", "cancelled"' },
          { name: "created_after", type: "string", desc: "Date (YYYY-MM-DD)" },
          { name: "created_before", type: "string", desc: "Date (YYYY-MM-DD)" },
          { name: "page", type: "number", desc: "Page number (default: 1)" },
          { name: "per_page", type: "number", desc: "Results per page (default: 25, max: 100)" },
        ]}
        response={`{
  "data": [
    {
      "reference_number": "48291037",
      "status": "paid",
      "delivery_status": "enroute",
      ...
    }
  ]
}`}
      />

      <Endpoint
        method="GET"
        path="/orders/:reference_number"
        description="Get order details by reference number"
        response={`{
  "data": {
    "reference_number": "48291037",
    "status": "paid",
    "delivery_status": "delivered",
    "pickup_location": "123 Main St, JHB",
    "drop_off_location": "45 Oak Ave, Polokwane",
    "waybills": [ ... ],
    ...
  }
}`}
      />

      <Endpoint
        method="POST"
        path="/orders/:reference_number/cancel"
        description="Cancel an order"
        response={`{
  "data": {
    "reference_number": "48291037",
    "status": "cancelled",
    "delivery_status": "cancelled",
    ...
  }
}`}
      />
    </div>
  </div>
);

const WaybillsSection = () => (
  <div className="space-y-4 text-gray-700">
    <h2 className="text-2xl font-bold text-purple-800 mb-4">Waybills</h2>
    <div className="space-y-3">
      <Endpoint
        method="GET"
        path="/orders/:reference_number/waybills"
        description="List waybills for an order"
        response={`{
  "data": [
    {
      "id": 101,
      "sub_reference": "48291037-01",
      "status": "pending",
      "receiver_full_name": "John Doe",
      "receiver_cellphone": "0712345678",
      "collection_point": "Midrand Hub",
      "collection_invoice_number": "INV-001",
      "created_at": "2024-12-15T10:30:00Z"
    }
  ]
}`}
      />

      <Endpoint
        method="GET"
        path="/orders/:reference_number/waybills/:id/pdf"
        description="Download waybill PDF"
        params={[
          { name: "format_type", type: "string", desc: '"a5" (default) or "thermal" (4x6 label)' },
        ]}
      >
        <p className="text-sm text-gray-600">Returns a PDF file. Use <code className="bg-gray-100 px-1 rounded">format_type=thermal</code> for 4x6 thermal printer labels.</p>
      </Endpoint>
    </div>
  </div>
);

const ReferenceSection = () => (
  <div className="space-y-10 text-gray-700">
    <section>
      <h2 className="text-2xl font-bold text-purple-800 mb-4">Status Reference</h2>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-bold text-gray-600 uppercase mb-2">Payment Status</h3>
          <div className="space-y-1 text-sm">
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">created</code> — awaiting payment</div>
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">paid</code> — payment received</div>
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">cancelled</code> — order cancelled</div>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-600 uppercase mb-2">Delivery Status</h3>
          <div className="space-y-1 text-sm">
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">pending</code> — awaiting collection</div>
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">collected</code> — picked up from sender</div>
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">enroute</code> — on the way</div>
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">ready_for_collection</code> — at destination hub</div>
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">delivered</code> — delivered to receiver</div>
            <div className="flex gap-2"><code className="text-purple-700 bg-purple-50 px-1.5 rounded">cancelled</code> — delivery cancelled</div>
          </div>
        </div>
      </div>
    </section>

    <section>
      <h2 className="text-2xl font-bold text-purple-800 mb-4">Error Codes</h2>
      <div className="space-y-1 text-sm">
        <div className="flex gap-3"><code className="text-gray-700 bg-gray-100 px-1.5 rounded">401</code> — Invalid or missing API key</div>
        <div className="flex gap-3"><code className="text-gray-700 bg-gray-100 px-1.5 rounded">404</code> — Resource not found</div>
        <div className="flex gap-3"><code className="text-gray-700 bg-gray-100 px-1.5 rounded">422</code> — Validation error (check <code className="bg-gray-100 px-1 rounded">details</code> array)</div>
        <div className="flex gap-3"><code className="text-gray-700 bg-gray-100 px-1.5 rounded">429</code> — Rate limit exceeded</div>
      </div>
    </section>
  </div>
);

const ShopifySection = () => (
  <div className="space-y-6 text-gray-700">
    <h2 className="text-2xl font-bold text-purple-800 mb-4">Shopify Integration</h2>
    <p className="mb-4">
      Connect your Shopify store to Parcelin so customers see our delivery rates at checkout and orders are automatically created when they pay.
    </p>

    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Step 1: Create a Custom App in Shopify</h3>
        <ol className="list-decimal pl-6 space-y-2 text-sm text-gray-700">
          <li>Go to your Shopify Admin {"→"} <strong>Settings {"→"} Apps and sales channels</strong></li>
          <li>Click <strong>Develop apps</strong> (enable custom app development if prompted)</li>
          <li>Click <strong>Create an app</strong> and name it "Parcelin"</li>
          <li>Under <strong>Configuration {"→"} Admin API</strong>, grant these scopes:
            <div className="flex flex-wrap gap-1.5 mt-1">
              {['read_orders', 'write_fulfillments', 'write_shipping'].map(s => (
                <code key={s} className="text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded text-xs">{s}</code>
              ))}
            </div>
          </li>
          <li>Click <strong>Install app</strong> and copy the <strong>Admin API access token</strong></li>
        </ol>
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Step 2: Send Us Your Details</h3>
        <p className="text-sm text-gray-700 mb-2">
          Email us at{" "}
          <a href="mailto:info@parcelin.co.za" className="text-purple-600 underline">info@parcelin.co.za</a>
          {" "}with:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
          <li>Your <strong>Shopify store domain</strong> (e.g. my-store.myshopify.com)</li>
          <li>Your <strong>Admin API access token</strong> from Step 1</li>
          <li>Your <strong>Parcelin business name</strong></li>
        </ul>
        <p className="text-sm text-gray-500 mt-2">
          We'll connect everything on our end and confirm when it's live.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">What Happens After Setup</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-2xl mb-2">{"🛒"}</div>
            <p className="text-sm font-semibold text-gray-800">Checkout Rates</p>
            <p className="text-xs text-gray-500 mt-1">Customers see Parcelin delivery options and prices at checkout</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-2xl mb-2">{"📦"}</div>
            <p className="text-sm font-semibold text-gray-800">Auto-Create Orders</p>
            <p className="text-xs text-gray-500 mt-1">When a customer pays, a Parcelin order and waybill are created automatically</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-2xl mb-2">{"📍"}</div>
            <p className="text-sm font-semibold text-gray-800">Live Tracking</p>
            <p className="text-xs text-gray-500 mt-1">Delivery status updates sync back to Shopify so customers can track their parcel</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const WooCommerceSection = () => (
  <div className="space-y-6 text-gray-700">
    <h2 className="text-2xl font-bold text-purple-800 mb-4">WooCommerce Integration</h2>
    <p className="mb-4">
      Connect your WooCommerce (WordPress) store to Parcelin so orders are automatically created when customers complete checkout.
    </p>

    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Step 1: Generate WooCommerce API Keys</h3>
        <ol className="list-decimal pl-6 space-y-2 text-sm text-gray-700">
          <li>Go to your WordPress Admin {"→"} <strong>WooCommerce {"→"} Settings {"→"} Advanced {"→"} REST API</strong></li>
          <li>Click <strong>Add key</strong></li>
          <li>Set description to "Parcelin", user to your admin account, and permissions to <strong>Read/Write</strong></li>
          <li>Click <strong>Generate API key</strong></li>
          <li>Copy both the <strong>Consumer Key</strong> and <strong>Consumer Secret</strong> (you won't see the secret again)</li>
        </ol>
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Step 2: Create a Webhook</h3>
        <ol className="list-decimal pl-6 space-y-2 text-sm text-gray-700">
          <li>Go to <strong>WooCommerce {"→"} Settings {"→"} Advanced {"→"} Webhooks</strong></li>
          <li>Click <strong>Add webhook</strong></li>
          <li>Configure:
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li><strong>Name:</strong> Parcelin Order Sync</li>
              <li><strong>Status:</strong> Active</li>
              <li><strong>Topic:</strong> Order completed</li>
              <li><strong>Delivery URL:</strong>
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs block mt-1 break-all">
                  https://api.liftsearch.co.za/api/v1/woocommerce/webhooks
                </code>
              </li>
              <li><strong>Secret:</strong> Choose a strong secret (you'll send this to us)</li>
              <li><strong>API version:</strong> WP REST API Integration v3</li>
            </ul>
          </li>
          <li>Click <strong>Save webhook</strong></li>
        </ol>
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">Step 3: Send Us Your Details</h3>
        <p className="text-sm text-gray-700 mb-2">
          Email us at{" "}
          <a href="mailto:info@parcelin.co.za" className="text-purple-600 underline">info@parcelin.co.za</a>
          {" "}with:
        </p>
        <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
          <li>Your <strong>WooCommerce store URL</strong> (e.g. https://mystore.co.za)</li>
          <li>Your <strong>Consumer Key</strong> and <strong>Consumer Secret</strong> from Step 1</li>
          <li>Your <strong>Webhook Secret</strong> from Step 2</li>
          <li>Your <strong>Parcelin business name</strong></li>
        </ul>
        <p className="text-sm text-gray-500 mt-2">
          We'll connect everything on our end and confirm when it's live.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-2">What Happens After Setup</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-2xl mb-2">{"📦"}</div>
            <p className="text-sm font-semibold text-gray-800">Auto-Create Orders</p>
            <p className="text-xs text-gray-500 mt-1">When a customer completes checkout, a Parcelin order and waybill are created automatically</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-2xl mb-2">{"📍"}</div>
            <p className="text-sm font-semibold text-gray-800">Live Tracking</p>
            <p className="text-xs text-gray-500 mt-1">Delivery updates are posted as order notes in WooCommerce with tracking links</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="text-2xl mb-2">{"✅"}</div>
            <p className="text-sm font-semibold text-gray-800">Auto-Complete</p>
            <p className="text-xs text-gray-500 mt-1">Orders are automatically marked as completed in WooCommerce when delivered</p>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <h4 className="text-sm font-bold text-amber-800 mb-1">Shipping Rates at Checkout</h4>
        <p className="text-xs text-gray-700">
          To show Parcelin rates at WooCommerce checkout, add a <strong>Flat Rate</strong> shipping method under WooCommerce {"→"} Settings {"→"} Shipping {"→"} Shipping Zones.
          Set the cost to your Parcelin rate and name it "Parcelin Courier". We can help you configure this — just ask.
        </p>
      </div>
    </div>
  </div>
);

const SECTIONS = {
  "getting-started": GettingStartedSection,
  "parcels": ParcelsSection,
  "orders": OrdersSection,
  "waybills": WaybillsSection,
  "reference": ReferenceSection,
  "shopify": ShopifySection,
  "woocommerce": WooCommerceSection,
};

export const DevelopersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hash = location.hash.replace("#", "") || "getting-started";
  const activeSection = SECTIONS[hash] ? hash : "getting-started";
  const ActiveComponent = SECTIONS[activeSection];

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleNav = (key) => {
    navigate(`/developers#${key}`, { replace: true });
    setMobileNavOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-cyan-100 to-fuchsia-200 py-12 px-5">
      <SEO title="API Documentation" description="Parcelin API documentation for business integrations. Create orders, track deliveries, and manage waybills programmatically." path="/developers" />

      <div className="max-w-6xl mx-auto flex gap-6">
        {/* Side Navigation - Desktop */}
        <nav className="hidden md:block w-56 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-lg p-4 sticky top-6">
            <h2 className="text-sm font-bold text-purple-800 uppercase tracking-wider mb-3">Documentation</h2>
            <ul className="space-y-1">
              {NAV_ITEMS.map(({ key, label, icon }) => (
                <li key={key}>
                  <button
                    onClick={() => handleNav(key)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      activeSection === key
                        ? "bg-purple-100 text-purple-800 font-semibold"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                    }`}
                  >
                    <span>{icon}</span>
                    {label}
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <a href="mailto:info@parcelin.co.za" className="text-xs text-purple-600 hover:underline">Need help? Contact us</a>
            </div>
          </div>
        </nav>

        {/* Mobile Navigation Toggle */}
        <div className="md:hidden fixed bottom-4 right-4 z-50">
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="bg-purple-700 text-white rounded-full w-12 h-12 shadow-lg flex items-center justify-center"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileNavOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation Panel */}
        {mobileNavOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setMobileNavOpen(false)}>
            <div className="absolute bottom-20 right-4 bg-white rounded-xl shadow-xl p-4 w-56" onClick={(e) => e.stopPropagation()}>
              <ul className="space-y-1">
                {NAV_ITEMS.map(({ key, label, icon }) => (
                  <li key={key}>
                    <button
                      onClick={() => handleNav(key)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                        activeSection === key
                          ? "bg-purple-100 text-purple-800 font-semibold"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <span>{icon}</span>
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-purple-800 mb-2">
                {NAV_ITEMS.find(i => i.key === activeSection)?.label || "API Documentation"}
              </h1>
              <p className="text-gray-500">Integrate your systems with Parcelin's courier services.</p>
            </div>

            <ActiveComponent />

            {/* Bottom navigation */}
            <div className="mt-12 pt-6 border-t border-gray-200 flex justify-between">
              {(() => {
                const idx = NAV_ITEMS.findIndex(i => i.key === activeSection);
                const prev = idx > 0 ? NAV_ITEMS[idx - 1] : null;
                const next = idx < NAV_ITEMS.length - 1 ? NAV_ITEMS[idx + 1] : null;
                return (
                  <>
                    {prev ? (
                      <button onClick={() => handleNav(prev.key)} className="text-sm text-purple-600 hover:underline flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        {prev.label}
                      </button>
                    ) : <div />}
                    {next ? (
                      <button onClick={() => handleNav(next.key)} className="text-sm text-purple-600 hover:underline flex items-center gap-1">
                        {next.label}
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    ) : <div />}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
